// Everything that changes app state. Local first: IndexedDB and the store update immediately,
// the network follows through the sync engine's outboxes.
import type {
  CardRow,
  LessonProgressRow,
  MeResponse,
  NewFlag,
  NotebookDone,
  NotebookTasksPerDay,
  ReviewRow,
  SharedSettings,
  SyncPull,
  UserInfo,
} from '@valyrio/engine/api';
import type { CardType, DictionaryBundle, Gloss, Lemma, PaceConfig, UiLang } from '@valyrio/engine/types';
import type { RecCoachKey } from '@valyrio/engine/coach/rec-prefs';
import { api, ApiError, onLoginRequired } from '../lib/api';
import {
  getDB,
  kvGet,
  kvSet,
  onDbEvent,
  requestPersistentStorage,
  toCardRow,
  type CardRecord,
  type LocalFlag,
  type OutboxWrite,
  type ReviewVoid,
  type ValyrioDB,
} from '../lib/db';
import { courseWords, fetchCourseOrder, fetchCoursePlan, planWords } from '../lib/course';
import { buildDictionary, emptyOverlay, type DictOverlay } from '../lib/dictionary';
import { emitCardsChanged } from '../lib/cardEvents';
import { applyGrade, cardKey, emptyStoredCard, type GradeValue, type Preview } from '../lib/fsrs';
import { disablePush, reconcilePush } from '../lib/push';
import { loadRemote } from '../lib/remote';
import { estimateShared, paceFromShared, paceWithOpens, paceWithRises } from '../lib/shared';
import { enqueueWrite, recordLessonProgress, recordReview, SyncEngine, uid } from '../lib/sync';
import { todayLocal } from '../lib/time';
import { perfMark, timed, timedSync } from '../lib/perf';
import { addDays as addDaysLocal } from '@valyrio/engine/course';
import { DEFAULT_SETTINGS, store, type AppState, type Theme } from './store';
// phone health (2026-10-09): the error log and heartbeat queued before each sync's flush
import { queueHealthReport } from '../health/report';

let db: ValyrioDB | null = null;
let sync: SyncEngine | null = null;
let stopSync: (() => void) | null = null;
void stopSync;

export function getSync(): SyncEngine | null {
  return sync;
}

/** Tests only: the connection gradeCard/undoGrade write to (no initApp, no sync engine). */
export function __setDbForTests(d: ValyrioDB | null): void {
  db = d;
}

// ---------- bundle ----------

/**
 * The network copy of the bundle (IndexedDB holds the only stored copy; the service worker does
 * not cache it). With `knownVersion`, only the start of the file is read when it carries the same
 * `version` (the first field): 'same' — a warm start then never parses the 3 MB bundle a second
 * time. 'offline': the network could not be reached; null: the server has no bundle.
 */
async function fetchBundle(knownVersion?: string): Promise<DictionaryBundle | 'same' | 'offline' | null> {
  let res: Response;
  try {
    res = await fetch('/data/bundle.json', { cache: 'no-cache' });
  } catch {
    return 'offline';
  }
  // A gateway or service-worker failure while offline is not "no bundle on the server".
  if (res.status === 0 || res.status === 502 || res.status === 503 || res.status === 504) return 'offline';
  try {
    const type = res.headers.get('content-type') ?? '';
    // Pages answers a missing file with the SPA's index.html (200, text/html): treat as missing.
    if (!res.ok || !type.includes('json')) return null;
    let b: DictionaryBundle;
    if (knownVersion && res.body && typeof TextDecoder !== 'undefined') {
      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      const dec = new TextDecoder();
      let head = '';
      let checked = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        if (!checked) {
          head += dec.decode(value, { stream: true });
          const m = /"version"\s*:\s*"([^"]*)"/.exec(head);
          if (m || head.length > 4096) {
            checked = true;
            if (m && m[1] === knownVersion) {
              await reader.cancel().catch(() => undefined);
              return 'same';
            }
          }
        }
      }
      const text = await new Blob(chunks as BlobPart[]).text();
      b = timedSync('bundle-parse', () => JSON.parse(text) as DictionaryBundle);
    } else {
      const text = await res.text();
      b = timedSync('bundle-parse', () => JSON.parse(text) as DictionaryBundle);
    }
    return Array.isArray(b?.lemmas) ? b : null;
  } catch (e) {
    // a broken file is "no bundle"; the connection dropping mid-download is "offline"
    return e instanceof SyntaxError ? null : 'offline';
  }
}

async function loadFixture(): Promise<DictionaryBundle> {
  const m = await import('../dev/fixture-bundle');
  return m.fixtureBundle;
}

function setDictionary(bundle: DictionaryBundle, overlay: DictOverlay | undefined, source: 'bundle' | 'fixture') {
  store.set({ dict: timedSync('dict-build', () => buildDictionary(bundle, overlay ?? null)), bundleSource: source });
  perfMark('dict-ready');
}

/**
 * Cached bundle first (instant, offline); then the network copy if it is newer. With a cached copy
 * this returns at once and the freshness check runs in the background (the start-up path reads
 * and parses the bundle once).
 */
async function loadBundle(d: ValyrioDB): Promise<void> {
  const [cached, overlay, source] = await timed('bundle-idb', () => Promise.all([d.get('bundle', 'base'), kvGet(d, 'dictOverlay'), kvGet(d, 'bundleSource')]));
  if (cached) {
    setDictionary(cached, overlay, source ?? 'bundle');
    void refreshBundle(d, cached, overlay, source).catch(() => undefined);
    return;
  }
  await refreshBundle(d, cached, overlay, source);
}

async function refreshBundle(
  d: ValyrioDB,
  cached: DictionaryBundle | undefined,
  overlay: DictOverlay | undefined,
  source: 'bundle' | 'fixture' | undefined,
): Promise<void> {
  const got = await timed('bundle-net', () => fetchBundle(cached && source !== 'fixture' ? cached.version : undefined));
  if (got === 'same') return;
  if (got === 'offline') {
    // With a cached copy nothing is missing (the version check waits for the next start). A new
    // device without one says so and fetches the dictionary as soon as it is online.
    if (!cached) waitForDictionary(() => refreshBundle(d, undefined, overlay, undefined));
    return;
  }
  const fresh = got;
  if (fresh) {
    if (store.get().dictMissing) store.set({ dictMissing: null });
    if (!cached || cached.version !== fresh.version || source === 'fixture') {
      await d.put('bundle', fresh, 'base');
      await kvSet(d, 'bundleSource', 'bundle');
      setDictionary(fresh, overlay, 'bundle');
    }
    return;
  }
  if (!cached) {
    // TODO(data): the import agent's bundle is not in app/public/data/ yet. Dev fixture only.
    const fixture = await loadFixture();
    await d.put('bundle', fixture, 'base');
    await kvSet(d, 'bundleSource', 'fixture');
    setDictionary(fixture, overlay, 'fixture');
  }
}

/**
 * Course word order: the course plan (/data/course-plan.json — lesson words + vocabulary quotas,
 * the same lists D1's lesson_item holds) when present, else the catalogue built from
 * course-order.json units and the MDX metas. Cached copies first, then newer network copies.
 */
async function loadCourse(d: ValyrioDB): Promise<void> {
  const [cachedOrder, cachedPlan] = await timed('course-idb', () => Promise.all([kvGet(d, 'courseOrder'), kvGet(d, 'coursePlan')]));
  // courseWords() also records the units the lesson list shows; call it even when a plan wins.
  const fromOrder = cachedOrder ? courseWords(cachedOrder) : null;
  if (cachedPlan) store.set({ course: planWords(cachedPlan) });
  else if (fromOrder) store.set({ course: fromOrder });
  // With a cached copy, check for a newer one in the background (start-up does not wait).
  if (cachedPlan || cachedOrder) {
    void refreshCourse(d, cachedOrder, cachedPlan).catch(() => undefined);
    return;
  }
  await refreshCourse(d, cachedOrder, cachedPlan);
}

async function refreshCourse(
  d: ValyrioDB | null,
  cachedOrder: Awaited<ReturnType<typeof fetchCourseOrder>> | undefined,
  cachedPlan: Awaited<ReturnType<typeof fetchCoursePlan>> | undefined,
): Promise<void> {
  const [order, plan] = await timed('course-net', () => Promise.all([fetchCourseOrder(), fetchCoursePlan()]));
  let changed = false;
  if (order && (!cachedOrder || cachedOrder.version !== order.version)) {
    if (d) await kvSet(d, 'courseOrder', order);
    changed = true;
  }
  if (plan && (!cachedPlan || cachedPlan.version !== plan.version)) {
    if (d) await kvSet(d, 'coursePlan', plan);
    changed = true;
  }
  if (!changed) return;
  const units = order ?? cachedOrder;
  const words = units ? courseWords(units) : null;
  const p = plan ?? cachedPlan;
  if (p) store.set({ course: planWords(p) });
  else if (words) store.set({ course: words });
}

// --- data refresh on return to the app (shell/UpdatePrompt.tsx, shell/update-policy.ts) ---
// An installed app can stay open for days; a deploy's new dictionary bundle or course plan would
// otherwise wait for the next cold start. Same version checks as start-up (a same-version bundle is
// only read up to its "version" field).
export async function refreshData(): Promise<void> {
  const d = db;
  if (!d || store.get().inSession) return;
  const [cached, overlay, source, cachedOrder, cachedPlan] = await Promise.all([
    d.get('bundle', 'base'),
    kvGet(d, 'dictOverlay'),
    kvGet(d, 'bundleSource'),
    kvGet(d, 'courseOrder'),
    kvGet(d, 'coursePlan'),
  ]);
  await Promise.all([refreshBundle(d, cached, overlay, source).catch(() => undefined), refreshCourse(d, cachedOrder, cachedPlan).catch(() => undefined)]);
}

/**
 * Before the app reloads into a new version: grades still in memory go to IndexedDB, and a
 * reachable server gets the outbox (at most `maxMs`; what is left stays queued in IndexedDB).
 */
export async function flushBeforeUpdate(maxMs = 5000): Promise<void> {
  await flushUnsaved().catch(() => undefined);
  if (!sync || store.get().identity === 'demo' || store.get().sync.pending === 0) return;
  await Promise.race([sync.syncNow().catch(() => undefined), new Promise((r) => setTimeout(r, maxMs))]);
}
// --- end data refresh ---

// ---------- identity ----------

function applyMe(r: MeResponse, identity: 'server' | 'cached' | 'demo', keepLocalPrefs: boolean) {
  const s = store.get();
  const theme = keepLocalPrefs ? s.theme : r.user.theme;
  const lang = keepLocalPrefs ? s.lang : r.user.uiLang;
  // Ääntämisvalmentaja on/off set on this device and still queued: kept like the theme (coach/coach.settings.tsx)
  const coach = keepLocalPrefs && s.me.coach !== undefined ? { coach: s.me.coach } : {};
  // "Ääntämisen apu äänityksissä" set on this device and still queued: kept the same way (coach/rec/rec-coach.settings.tsx)
  const recCoach = keepLocalPrefs && s.me.recCoach !== undefined ? { recCoach: s.me.recCoach } : {};
  store.set({
    identity,
    me: { ...r.user, theme, uiLang: lang, ...coach, ...recCoach },
    partner: r.partner,
    // with the lessons opened early that the last sync knew of and the stairs reached (lib/shared.ts paceFromShared)
    pace: paceFromShared(r.pace, s.shared),
    // A /me cached before shared settings existed has none: the defaults until the server answers.
    settings: { ...DEFAULT_SETTINGS, ...r.settings },
    theme,
    lang,
  });
  persistPrefs(theme, lang);
}

function persistPrefs(theme: Theme, lang: UiLang) {
  try {
    localStorage.setItem('valyrio:theme', theme);
    localStorage.setItem('valyrio:lang', lang);
  } catch {
    /* private mode */
  }
}

async function hasPendingMeWrite(d: ValyrioDB): Promise<boolean> {
  return (await d.getAll('writeOutbox')).some((w) => w.kind === 'me');
}

// ---------- init ----------

let started = false;

/**
 * Open IndexedDB; one retry (WebKit loses the connection after backgrounding now and then).
 * null = no storage on this device (private mode, disabled, broken): the app runs in memory.
 */
async function openStorage(): Promise<ValyrioDB | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return await getDB();
    } catch (err) {
      console.warn('[storage] IndexedDB could not be opened', err);
    }
  }
  return null;
}

/** A connection whose browser closed it reopens on the next use. */
async function liveDb(): Promise<ValyrioDB | null> {
  if (db) return db;
  if (store.get().storage === 'memory') return null;
  try {
    db = await getDB();
    startSync(db);
    return db;
  } catch {
    return null;
  }
}

function watchStorage(): void {
  onDbEvent((e) => {
    if (e === 'blocked') {
      store.set({ storage: 'blocked' });
    } else if (e === 'versionchange') {
      // Another tab is upgrading the database: this tab closed its connection and reloads to the
      // new version once the grades in flight are written (they reopen the connection if needed).
      db = null;
      void flushUnsaved().finally(() => {
        if (typeof location !== 'undefined' && !import.meta.env.VITEST) location.reload();
      });
    } else if (e === 'closed') {
      db = null;
    }
  });
}

/** A new device without the dictionary retries this often while the browser claims to be online. */
const DICT_RETRY_MS = 30_000;

/**
 * No dictionary on this device and no network: say so (store.dictMissing) and try again when the
 * browser comes online, or after DICT_RETRY_MS (captive Wi-Fi, a dead zone: no 'online' event
 * comes). One pending retry at a time.
 */
let dictRetry: (() => void) | null = null;
function waitForDictionary(retry: () => Promise<void>): void {
  store.set({ dictMissing: 'offline' });
  if (dictRetry || typeof window === 'undefined') return;
  const timer = setTimeout(() => dictRetry?.(), DICT_RETRY_MS);
  dictRetry = () => {
    window.removeEventListener('online', dictRetry!);
    clearTimeout(timer);
    dictRetry = null;
    void retry().catch(() => undefined);
  };
  window.addEventListener('online', dictRetry);
}

/** IndexedDB is unavailable: dictionary and course from the network/SW, grades kept in memory. */
async function initInMemory(): Promise<void> {
  store.set({ storage: 'memory' });
  const fresh = await fetchBundle();
  if (fresh === 'offline') {
    waitForDictionary(async () => {
      const again = await fetchBundle();
      if (again === 'offline') waitForDictionary(async () => undefined);
      else if (again && again !== 'same') {
        store.set({ dictMissing: null });
        setDictionary(again, undefined, 'bundle');
      }
    });
  } else if (fresh && fresh !== 'same') setDictionary(fresh, undefined, 'bundle');
  else setDictionary(await loadFixture(), undefined, 'fixture');
  await refreshCourse(null, undefined, undefined).catch(() => undefined);
  store.set({ phase: 'ready' });
  try {
    applyMe(await api.me(), 'server', false);
  } catch {
    /* offline: the fallback pace, and Today says it is waiting for the course state */
  }
}

export async function initApp(): Promise<void> {
  if (started) return;
  started = true;
  // Any request that finds the session gone (expired, logged out elsewhere, password changed on
  // another device) shows the login screen. The data on this device stays until someone else
  // signs in here (refreshMe) or this person signs out (signOut).
  onLoginRequired(() => store.set({ needsLogin: true }));
  watchStorage();
  listenOtherTabs();
  const opened = await openStorage();
  if (store.get().storage === 'blocked') store.set({ storage: 'ok' });
  if (!opened) {
    await initInMemory();
    return;
  }
  const d = (db = opened);

  const [me, shared, sharedAt, cardRecs, lessonProgress, todayCount, flags, inbox, dead, seen, notebook] = await Promise.all([
    kvGet(d, 'me'),
    kvGet(d, 'shared'),
    kvGet(d, 'sharedAt'),
    d.getAll('cards'),
    kvGet(d, 'lessonProgress'),
    kvGet(d, 'todayLocal'),
    d.getAll('localFlags'),
    kvGet(d, 'inbox'),
    kvGet(d, 'deadWrites'),
    kvGet(d, 'lettersSeenAt'),
    kvGet(d, 'notebook'),
  ]);
  if (me) applyMe(me, 'cached', true);
  // Letters seen "now" on a device's first start: the inbox counts what arrives from here on.
  if (!seen) await kvSet(d, 'lettersSeenAt', new Date().toISOString());
  store.set({
    shared: shared ?? null,
    sharedAt: sharedAt ?? null,
    cards: new Map(cardRecs.map((r) => [r.key, toCardRow(r)])),
    lessonProgress: lessonProgress ?? [],
    todayLocal: todayCount ?? { date: '', reviews: 0, newWords: 0 },
    localFlags: new Map(flags.map((f) => [f.key, f])),
    notebookDone: Array.isArray(notebook) ? notebook : [],
    inbox: inbox ?? null,
    rejected: dead ?? [],
  });
  // the cached shared state may know of a lesson opened early after the cached /me was read
  store.set((st) => ({ pace: paceFromShared(st.pace, st.shared) }));
  // flags refused while an older version ran stayed "unsent" on the device: drop them now
  for (const key of refusedFlagKeys(dead ?? [], flags)) await forgetLocalFlag(key);

  // Who am I? Asked in parallel with the dictionary (a cold start no longer waits for the bundle
  // before /api/me); never blocks the UI: a cached identity is already applied.
  const identityKnown = refreshMe(d);
  await Promise.all([loadBundle(d), loadCourse(d)]);
  store.set({ phase: 'ready' });
  perfMark('ready');
  // Installed app: ask for storage that is not evicted under disk pressure (the outbox holds
  // grades not yet sent). In a browser tab this waits for the first grade.
  if (isStandalone()) requestPersistentStorage();
  // The sync starts once both are known (startSync needs phase 'ready' and the identity).
  void identityKnown.finally(() => startSync(d));
}

function isStandalone(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

/** (Re)start the sync engine on a connection (again after the browser closed the old one). */
function startSync(d: ValyrioDB): void {
  // The dev demo never talks to the API.
  if (store.get().identity === 'demo' || store.get().phase !== 'ready') return;
  stopSync?.();
  sync = new SyncEngine({
    db: d,
    api,
    isOnline: () => navigator.onLine,
    onStatus: (s) => store.set({ sync: s }),
    onPulled: (p, changed) => void onPulled(p, changed).then(() => emitCardsChanged(changed)),
    onDictionary: (overlay) => void onDictionary(overlay),
    onRejected: (w) => void onWriteRejected(w),
    bundleGeneratedAt: () => store.get().dict?.generatedAt ?? null,
    // phone health: errors and a heartbeat go with the sync (health/report.ts, 2026-10-09)
    beforeFlush: () => queueHealthReport(d),
  });
  stopSync = sync.start();
}

function whenReady(): Promise<void> {
  if (store.get().phase === 'ready') return Promise.resolve();
  return new Promise((resolve) => {
    const off = store.subscribe(() => {
      if (store.get().phase !== 'ready') return;
      off();
      resolve();
    });
  });
}

async function refreshMe(d: ValyrioDB): Promise<void> {
  try {
    const r = await api.me();
    const prev = await kvGet(d, 'me');
    if ((prev && prev.user.id !== r.user.id) || (!prev && (await kvGet(d, 'demoSeeded')))) {
      await clearUserData(d);
    }
    await kvSet(d, 'me', r);
    applyMe(r, 'server', await hasPendingMeWrite(d));
    // Reminders: re-register this device's push subscription (renewed if the server's VAPID key
    // changed or it expires soon). Never prompts; failures wait for the next start.
    void reconcilePush();
  } catch (err) {
    if (err instanceof ApiError && err.loginRequired) return; // the login screen (onLoginRequired)
    if (store.get().identity !== 'local') return; // cached identity is fine offline
    if (import.meta.env.DEV && err instanceof ApiError && !err.auth) {
      const { enterDemo } = await import('../dev/demo');
      await whenReady(); // the demo estimate reads the dictionary (/api/me now runs alongside it)
      try {
        await enterDemo(d);
      } catch (e) {
        // The connection was closed under the demo seeding (another tab or a test deleted the
        // database): the demo seeds again on the next start; never an unhandled rejection.
        console.warn('demo seeding stopped:', e);
      }
    }
  }
}

/**
 * A different person signed in on this device (or the dev demo gave way to a real account):
 * drop the previous person's cards, outboxes and counters. The dictionary stays.
 */
async function clearUserData(d: ValyrioDB): Promise<void> {
  const tx = d.transaction(['cards', 'reviewOutbox', 'writeOutbox', 'lessonOutbox', 'localFlags', 'kv'], 'readwrite');
  const kv = tx.objectStore('kv');
  await Promise.all([
    tx.objectStore('cards').clear(),
    tx.objectStore('reviewOutbox').clear(),
    tx.objectStore('writeOutbox').clear(),
    tx.objectStore('lessonOutbox').clear(),
    tx.objectStore('localFlags').clear(),
    ...(
      [
        'shared',
        'sharedAt',
        'lastSync',
        'lessonProgress',
        'todayLocal',
        'deadWrites',
        'demoSeeded',
        'inbox',
        'lettersSeenAt',
        'notebook',
        'notebookPlan',
      ] as const
    ).map((k) => kv.delete(k)),
    // Cached server lists (letters, flags …) belong to the previous person's session.
    kv.getAllKeys().then((keys) => Promise.all(keys.filter((k) => String(k).startsWith('remote:')).map((k) => kv.delete(k)))),
    tx.done,
  ]);
  store.set({
    cards: new Map(),
    shared: null,
    sharedAt: null,
    lessonProgress: [],
    localFlags: new Map(),
    todayLocal: { date: '', reviews: 0, newWords: 0 },
    inbox: null,
    rejected: [],
    notebookDone: [],
  });
}

async function onPulled(p: SyncPull, changed?: readonly CardRow[]): Promise<void> {
  if (!db) return;
  // Only the cards the pull replaced change in memory (re-reading every card from IndexedDB took
  // a long task on a one-year deck after each sync).
  let cards: Map<string, CardRow>;
  if (changed) {
    cards = store.get().cards;
    if (changed.length) {
      cards = new Map(cards);
      for (const c of changed) cards.set(cardKey(c.type, c.itemId), c);
    }
  } else {
    cards = new Map((await db.getAll('cards')).map((r) => [r.key, toCardRow(r)]));
  }
  // Merge, never drop: the server skips rows for lessons it does not know yet (lesson table not
  // seeded with that id), and a pass recorded here must not vanish because of that.
  const lessonProgress = sameOr(store.get().lessonProgress, mergeLessonProgress(store.get().lessonProgress, p.lessonProgress));
  await kvSet(db, 'lessonProgress', lessonProgress);
  store.set((st) => ({
    // a lesson opened early (both said yes) moves every date at once (lib/shared.ts paceFromShared; the stairs reached too)
    pace: sameOr(st.pace, paceFromShared(st.pace, p.shared)),
    cards,
    shared: sameOr(st.shared, p.shared),
    sharedAt: p.now,
    lessonProgress,
    ...(p.inbox ? { inbox: sameOr(st.inbox, p.inbox) } : {}),
  }));
}

/**
 * The previous value when the new one is the same data (a sync that changed nothing): every
 * screen that reads it then keeps its render. A sync runs after each queued write (reading
 * progress while scrolling, a grade), and each one re-rendered every reader of these.
 */
function sameOr<T>(prev: T, next: T): T {
  if (prev === next) return prev;
  try {
    return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
  } catch {
    return next;
  }
}

/** Union by lesson: earliest pass, best score, most attempts (the server merges the same way). */
export function mergeLessonProgress(local: readonly LessonProgressRow[], remote: readonly LessonProgressRow[]): LessonProgressRow[] {
  const by = new Map<string, LessonProgressRow>();
  for (const r of [...local, ...remote]) {
    const prev = by.get(r.lessonId);
    if (!prev) {
      by.set(r.lessonId, { ...r });
      continue;
    }
    const passes = [prev.passedAt, r.passedAt].filter((x): x is string => !!x).sort();
    by.set(r.lessonId, {
      lessonId: r.lessonId,
      passedAt: passes[0] ?? null,
      bestScore: Math.max(prev.bestScore ?? 0, r.bestScore ?? 0),
      attempts: Math.max(prev.attempts, r.attempts),
    });
  }
  return [...by.values()];
}

async function onDictionary(overlay: DictOverlay): Promise<void> {
  if (!db) return;
  const [bundle, source] = await Promise.all([db.get('bundle', 'base'), kvGet(db, 'bundleSource')]);
  if (bundle) setDictionary(bundle, overlay, source ?? 'bundle');
}

/** Estimate shared state when the server has never been reached. */
export function ensureEstimatedShared(totalWords: number): void {
  const s = store.get();
  if (s.shared) return;
  store.set({ shared: estimateShared(s.pace, new Date(), totalWords, s.me.id), sharedAt: new Date().toISOString() });
}

// ---------- study ----------

export interface GradeArgs {
  itemId: string;
  type: CardType;
  grade: GradeValue;
  /** 'exercise': a self-marked notebook sentence grading the word's card (notebook/grading.ts). */
  mode: 'daily' | 'free' | 'exercise';
  preview?: Preview;
  newWord: boolean;
  /** Extra fields for the review's log (what the grade came from, e.g. { kind: 'notebook' }). */
  logExtra?: Record<string, unknown>;
}

/** A quiet push during a long session: after this many grades (debounced, never blocks the UI). */
export const PUSH_EVERY_GRADES = 10;
let gradesSincePush = 0;

/** What one grade changed, so it can be undone ("Kumoa", undoGrade). */
export interface GradeReceipt {
  key: string;
  /** The card before the grade (undefined = the grade introduced it). */
  prev: CardRow | undefined;
  card: CardRow;
  review: ReviewRow;
  /** Free practice on a card not due: only the review was recorded, the card is unchanged. */
  practice: boolean;
  newWord: boolean;
  todayBefore: AppState['todayLocal'];
  todayAfter: AppState['todayLocal'];
  demo: boolean;
  /** Settles when the first write attempt finished (true = on the device). */
  written: Promise<boolean>;
}

/** Local-first grade: store + IndexedDB now, the server on the next sync. Returns the new card. */
export function gradeCard(a: GradeArgs): CardRow {
  return grade(a).card;
}

/** gradeCard, returning what undoGrade needs. */
export function grade(a: GradeArgs): GradeReceipt {
  const s = store.get();
  const key = cardKey(a.type, a.itemId);
  const now = new Date();
  const prev = s.cards.get(key);
  const applied = applyGrade({
    existing: prev,
    itemId: a.itemId,
    type: a.type,
    grade: a.grade,
    mode: a.mode,
    now,
    id: uid(),
    tz: s.pace.timezone,
    ...(a.preview ? { preview: a.preview } : {}),
  });
  const { card, practice } = applied;
  const review: ReviewRow = a.logExtra ? { ...applied.review, log: { ...(applied.review.log as object), ...a.logExtra } } : applied.review;
  const date = todayLocal(now, s.pace.timezone);
  const t = s.todayLocal.date === date ? s.todayLocal : { date, reviews: 0, newWords: 0 };
  const todayCount = { ...t, date, reviews: t.reviews + 1, newWords: t.newWords + (a.newWord ? 1 : 0) };
  if (practice) {
    store.set({ todayLocal: todayCount });
  } else {
    const cards = new Map(s.cards);
    cards.set(key, card);
    store.set({ cards, todayLocal: todayCount });
  }
  const r: Omit<GradeReceipt, 'written'> = {
    key,
    prev,
    card,
    review,
    practice,
    newWord: a.newWord,
    todayBefore: s.todayLocal,
    todayAfter: todayCount,
    demo: s.identity === 'demo',
  };
  // Never awaited by the UI: motion and the next card do not wait for IndexedDB.
  let receipt: GradeReceipt;
  const written =
    s.storage === 'memory'
      ? Promise.resolve(false)
      : writeGrade(r).then(
          () => {
            void refreshPending();
            return true;
          },
          (err: unknown) => {
            console.warn('[storage] grade not written to the device; retrying', err);
            keepUnsaved(receipt);
            return false;
          },
        );
  receipt = { ...r, written };
  tellOtherTabs(practice ? [] : [card], [], todayCount);
  requestPersistentStorage();
  // The partner sees progress during a long session, and nothing is lost if the app is closed.
  if (!r.demo && ++gradesSincePush >= PUSH_EVERY_GRADES) {
    gradesSincePush = 0;
    sync?.request(5000);
  }
  return receipt;
}

async function writeGrade(r: Omit<GradeReceipt, 'written'>, latest = false): Promise<void> {
  const d = await liveDb();
  if (!d) throw new Error('IndexedDB is not available');
  // A retry writes the card as it is now (a later grade of the same card may have been written).
  const card = latest ? (store.get().cards.get(r.key) ?? r.card) : r.card;
  if (r.demo) {
    // The dev demo keeps its cards on the device and never queues anything for the API.
    if (!r.practice) await d.put('cards', { ...card, key: r.key, dirty: 0 });
  } else if (r.practice) {
    await d.put('reviewOutbox', r.review);
  } else {
    await recordReview(d, card, r.review);
  }
  await kvSet(d, 'todayLocal', latest ? store.get().todayLocal : r.todayAfter);
}

// ---------- grades that could not be written yet ----------

const unsaved: GradeReceipt[] = [];
let unsavedTimer: ReturnType<typeof setTimeout> | null = null;
let unsavedTry = 0;
const UNSAVED_RETRY_MS = [2_000, 5_000, 15_000, 60_000];

function keepUnsaved(r: GradeReceipt): void {
  unsaved.push(r);
  store.set({ unsaved: unsaved.length });
  scheduleUnsaved();
}

function scheduleUnsaved(): void {
  if (unsavedTimer || !unsaved.length) return;
  const delay = UNSAVED_RETRY_MS[Math.min(unsavedTry, UNSAVED_RETRY_MS.length - 1)]!;
  unsavedTimer = setTimeout(() => {
    unsavedTimer = null;
    unsavedTry++;
    void flushUnsaved().finally(scheduleUnsaved);
  }, delay);
}

/** Write the grades kept in memory after a failed IndexedDB write (oldest first). */
export async function flushUnsaved(): Promise<void> {
  while (unsaved.length) {
    try {
      await writeGrade(unsaved[0]!, true);
    } catch {
      db = null; // reopen on the next attempt
      return;
    }
    unsaved.shift();
    unsavedTry = 0;
    store.set({ unsaved: unsaved.length });
  }
  void refreshPending();
}

// ---------- undo ("Kumoa") ----------

/**
 * Undo one grade: the card as it was, the review gone. Not sent yet → the review leaves the
 * outbox. Possibly sent (no longer queued, or a sync was running) → the restored card is pushed
 * with a newer updatedAt and the review is listed in kv `reviewVoids` for the server to remove.
 */
export async function undoGrade(r: GradeReceipt): Promise<void> {
  // Strictly newer than the graded card, so the restored copy wins last-writer-wins everywhere.
  const nowIso = new Date(Math.max(Date.now(), Date.parse(r.card.updatedAt) + 1)).toISOString();
  const s = store.get();
  const today =
    s.todayLocal.date === r.todayAfter.date
      ? { ...s.todayLocal, reviews: Math.max(0, s.todayLocal.reviews - 1), newWords: Math.max(0, s.todayLocal.newWords - (r.newWord ? 1 : 0)) }
      : s.todayLocal;
  const i = unsaved.indexOf(r);
  const neverWritten = i >= 0 || s.storage === 'memory';
  if (i >= 0) {
    unsaved.splice(i, 1);
    store.set({ unsaved: unsaved.length });
  }
  // In memory first (the screen does not wait); the device copy follows.
  let restored: CardRow | undefined = r.prev;
  if (!r.practice) {
    const cards = new Map(s.cards);
    if (r.prev) {
      restored = { ...r.prev, updatedAt: nowIso };
      cards.set(r.key, restored);
    } else cards.delete(r.key);
    store.set({ cards, todayLocal: today });
  } else store.set({ todayLocal: today });
  if (neverWritten) {
    tellOtherTabs(restored && !r.practice ? [restored] : [], !r.prev && !r.practice ? [r.key] : [], today);
    return;
  }
  await r.written;
  const d = await liveDb();
  if (!d) return;
  if (r.demo) {
    if (!r.practice) await (r.prev ? d.put('cards', { ...r.prev, key: r.key, dirty: 0 }) : d.delete('cards', r.key));
    await kvSet(d, 'todayLocal', today);
    return;
  }
  const syncing = store.get().sync.state === 'syncing';
  const tx = d.transaction(['cards', 'reviewOutbox', 'kv'], 'readwrite');
  const outbox = tx.objectStore('reviewOutbox');
  const cards = tx.objectStore('cards');
  const kv = tx.objectStore('kv');
  const queued = !!(await outbox.get(r.review.id));
  if (queued) await outbox.delete(r.review.id);
  const maybeSent = !queued || syncing;
  if (!r.practice) {
    if (r.prev) {
      await cards.put({ ...r.prev, updatedAt: nowIso, key: r.key, dirty: 1 } satisfies CardRecord);
    } else if (!maybeSent) {
      await cards.delete(r.key);
    } else {
      // The server may have the new card already: send it back as never reviewed.
      const blank = emptyStoredCard(new Date(nowIso));
      restored = { ...r.card, due: blank.due, state: blank, updatedAt: nowIso };
      await cards.put({ ...restored, key: r.key, dirty: 1 });
      const m = new Map(store.get().cards);
      m.set(r.key, restored);
      store.set({ cards: m });
    }
  }
  if (maybeSent) {
    const voids = ((await kv.get('reviewVoids')) as ReviewVoid[] | undefined) ?? [];
    voids.push({
      id: r.review.id,
      itemId: r.review.itemId,
      type: r.review.type,
      reviewedAt: r.review.reviewedAt,
      isNew: r.review.isNew,
      voidedAt: nowIso,
    });
    await kv.put(voids, 'reviewVoids');
  }
  await kv.put(today, 'todayLocal');
  await tx.done;
  tellOtherTabs(restored && !r.practice ? [restored] : [], !restored && !r.practice ? [r.key] : [], today);
  void refreshPending();
  if (maybeSent) sync?.request(2000);
}

// ---------- other tabs of the app ----------

export interface TabMessage {
  kind: 'cards';
  userId: string;
  set: CardRow[];
  remove: string[];
  todayLocal: AppState['todayLocal'];
}

let channel: BroadcastChannel | null = null;

/**
 * Two tabs (or the installed app and a browser tab) share IndexedDB but not memory. Each grade is
 * announced, so the other tab's session grades from the current card (not a stale copy that
 * would overwrite this grade) and drops cards already done.
 */
function listenOtherTabs(): void {
  if (channel || typeof BroadcastChannel === 'undefined') return;
  try {
    channel = new BroadcastChannel('valyrio');
    (channel as unknown as { unref?: () => void }).unref?.();
    channel.onmessage = (e: MessageEvent<TabMessage>) => onTabMessage(e.data);
  } catch {
    channel = null;
  }
}

function tellOtherTabs(set: CardRow[], remove: string[], todayLocal: AppState['todayLocal']): void {
  if (!channel) return;
  try {
    channel.postMessage({ kind: 'cards', userId: store.get().me.id, set, remove, todayLocal } satisfies TabMessage);
  } catch {
    /* closed */
  }
}

/** Another tab graded or undid a card (exported for tests). */
export function onTabMessage(m: TabMessage): void {
  if (!m || m.kind !== 'cards' || m.userId !== store.get().me.id) return;
  const cards = new Map(store.get().cards);
  for (const c of m.set) cards.set(cardKey(c.type, c.itemId), c);
  for (const k of m.remove) cards.delete(k);
  store.set({ cards, todayLocal: m.todayLocal });
  emitCardsChanged(m.set);
}

async function refreshPending() {
  if (!db) return;
  try {
    const [r, w, l] = await Promise.all([db.count('reviewOutbox'), db.count('writeOutbox'), db.count('lessonOutbox')]);
    store.set((s) => ({ sync: { ...s.sync, pending: r + w + l } }));
  } catch {
    /* connection closed: counted again after the next write */
  }
}

export function setInSession(v: boolean): void {
  if (store.get().inSession !== v) store.set({ inSession: v });
  // Session chrome (styles/study.css): the install hint and the sync chip step aside on a phone.
  if (typeof document !== 'undefined') document.documentElement.classList.toggle('in-study', v);
}

/** Today's session on screen has this many cards left (the app badge reads it; null = none). */
export function setSessionLeft(n: number | null): void {
  if (store.get().sessionLeft !== n) store.set({ sessionLeft: n });
}

/** After a session: push now (fire and forget). */
export function sessionEnded(): void {
  gradesSincePush = 0;
  if (store.get().identity !== 'demo') sync?.request(0);
}

// ---------- personal settings (synced via PATCH /api/me) ----------

async function patchMe(body: Partial<Pick<UserInfo, 'uiLang' | 'theme' | 'notebookTasks' | 'coach' | 'recCoach'>> & { introSeen?: string }) {
  if (!db || store.get().identity === 'demo') return;
  await enqueueWrite(db, { method: 'PATCH', path: '/api/me', body, kind: 'me' });
  await refreshPending();
  syncSoon();
}

function syncSoon(): void {
  if (store.get().identity !== 'demo') sync?.request();
}

export function setTheme(theme: Theme): void {
  const s = store.get();
  store.set({ theme, me: { ...s.me, theme } });
  persistPrefs(theme, s.lang);
  void patchMe({ theme });
}

/** Vihkotehtäviä päivässä (0–3), personal: synced like the language and theme. */
export function setNotebookTasks(n: NotebookTasksPerDay): void {
  const s = store.get();
  store.set({ me: { ...s.me, notebookTasks: n } });
  void cacheMe((r) => ({ ...r, user: { ...r.user, notebookTasks: n } }));
  void patchMe({ notebookTasks: n });
}

/** Ääntämisvalmentaja on or off (Asetukset → Harjoittelu; coach/coach.settings.tsx), personal: synced like the theme. */
export function setCoach(on: boolean): void {
  const s = store.get();
  store.set({ me: { ...s.me, coach: on } });
  void cacheMe((r) => ({ ...r, user: { ...r.user, coach: on } }));
  void patchMe({ coach: on });
}

/**
 * One of "Ääntämisen apu äänityksissä" on or off (Asetukset → Harjoittelu; coach/rec/rec-coach.settings.tsx; engine
 * coach/rec-prefs.ts), personal: synced like the coach itself. Only the key changed is sent (the server merges).
 */
export function setRecCoach(key: RecCoachKey, on: boolean): void {
  const s = store.get();
  const recCoach = { ...s.me.recCoach, [key]: on };
  store.set({ me: { ...s.me, recCoach } });
  void cacheMe((r) => ({ ...r, user: { ...r.user, recCoach } }));
  void patchMe({ recCoach: { [key]: on } });
}

// --- one-time explainers (study/GradesIntro.tsx) ---

const introKey = (userId: string, id: string) => `valyrio:intro:${id}:${userId || 'me'}`;

/** This person has dismissed the explainer `id`: on the server (any device) or on this device. */
export function introSeen(me: Pick<UserInfo, 'id' | 'introsSeen'>, id: string): boolean {
  if (me.introsSeen?.includes(id)) return true;
  try {
    return localStorage.getItem(introKey(me.id, id)) === '1';
  } catch {
    return false;
  }
}

/**
 * Dismiss the explainer `id` for good: this device at once (also while the write waits offline, or
 * a stale /api/me overwrites the list), the cached /me, and the server (PATCH /api/me), so the
 * person's other device does not show it again.
 */
export function markIntroSeen(id: string): void {
  const s = store.get();
  try {
    localStorage.setItem(introKey(s.me.id, id), '1');
  } catch {
    /* the server's list still carries it */
  }
  if (s.me.introsSeen?.includes(id)) return;
  const introsSeen = [...(s.me.introsSeen ?? []), id];
  store.set({ me: { ...s.me, introsSeen } });
  void cacheMe((r) => ({ ...r, user: { ...r.user, introsSeen: [...new Set([...(r.user.introsSeen ?? []), id])] } }));
  void patchMe({ introSeen: id });
}

// --- end one-time explainers ---

export function setLang(lang: UiLang): void {
  const s = store.get();
  store.set({ lang, me: { ...s.me, uiLang: lang } });
  persistPrefs(s.theme, lang);
  void patchMe({ uiLang: lang });
}

// ---------- shared settings (the admin, online: they change the app for both at once) ----------

/** The server lists of who set each shared value and when (Asetukset's margin, settings/pages.tsx). */
export const SETTINGS_CHANGES_KEY = 'settings-changes';
export const PACE_HISTORY_KEY = 'pace-history';

/** Reminder time for both. Online only; throws the ApiError (shown where it was set). */
export async function saveSharedSettings(patch: Partial<SharedSettings>): Promise<void> {
  if (store.get().identity === 'demo') {
    store.set((s) => ({ settings: { ...s.settings, ...patch } }));
    return;
  }
  const settings = await api.putSettings(patch);
  store.set({ settings });
  await cacheMe((r) => ({ ...r, settings }));
  // "who set it and when" shows the change at once, not after the next sync
  void loadRemote(SETTINGS_CHANGES_KEY, () => api.settingsChanges());
}

/** The pace for both, applied at once. Online only; throws the ApiError. */
export async function savePace(pace: PaceConfig): Promise<void> {
  if (store.get().identity === 'demo') {
    store.set({ pace });
    return;
  }
  await api.setPace(pace);
  // the form's pace has no say over the lessons opened early: they stay (lib/shared.ts paceWithOpens)
  store.set((st) => ({ pace: paceWithRises(paceWithOpens(pace, st.pace.earlyOpens), st.pace.rises) }));
  await cacheMe((r) => ({ ...r, pace: paceWithOpens(pace, r.pace.earlyOpens) }));
  void loadRemote(PACE_HISTORY_KEY, () => api.pace());
  syncNow();
}

/** Keep the cached /me in step, so a restart offline shows what was just set. */
async function cacheMe(fn: (r: MeResponse) => MeResponse): Promise<void> {
  const d = await liveDb();
  if (!d) return;
  try {
    const r = await kvGet(d, 'me');
    if (r) await kvSet(d, 'me', fn(r));
  } catch {
    /* the next /api/me refreshes it */
  }
}

// ---------- flags (work offline via the outbox) ----------

export const flagKey = (t: NewFlag['target']) => `${t.table}:${t.id}`;

export async function raiseFlag(flag: NewFlag): Promise<void> {
  const rec: LocalFlag = { key: flagKey(flag.target), flag, createdAt: new Date().toISOString() };
  const flags = new Map(store.get().localFlags);
  flags.set(rec.key, rec);
  store.set({ localFlags: flags });
  if (!db) return;
  await db.put('localFlags', rec);
  if (store.get().identity !== 'demo') {
    await enqueueWrite(db, { method: 'POST', path: '/api/flags', body: flag, kind: 'flag' });
    await refreshPending();
    syncSoon();
  }
}

/**
 * Generic queued write for later sections (letters, proposals, …). `undo` (kept on the device,
 * never sent) is what onWriteRejected puts back if the server refuses the write.
 */
export async function queueWrite(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body: unknown,
  kind: string,
  opts?: { undo?: unknown; as?: { userId: string } | null },
): Promise<void> {
  if (!db) return;
  await enqueueWrite(db, {
    method,
    path,
    body,
    kind,
    ...(opts?.undo !== undefined ? { undo: opts.undo } : {}),
    // Yksi puhelin (together/onephone/): the partner's write on this phone, sent as theirs (X-Acting-As)
    ...(opts?.as ? { as: opts.as.userId } : {}),
  });
  await refreshPending();
  syncSoon();
}

/** The writes of one kind still waiting in the outbox, in queue order (lib/remote.ts overlays). */
export async function pendingWrites(kind: string): Promise<OutboxWrite[]> {
  if (!db) return [];
  return (await db.getAll('writeOutbox')).filter((w) => w.kind === kind);
}

export function syncNow(): void {
  if (store.get().identity !== 'demo') sync?.request(0);
}

/** Forget a rejected write (the person has read why). */
export async function dismissRejected(w: Pick<OutboxWrite, 'id' | 'createdAt'>): Promise<void> {
  const same = (x: OutboxWrite) => x.id === w.id && x.createdAt === w.createdAt;
  store.set((s) => ({ rejected: s.rejected.filter((x) => !same(x)) }));
  const d = db ?? (await getDB());
  await kvSet(
    d,
    'deadWrites',
    ((await kvGet(d, 'deadWrites')) ?? []).filter((x) => !same(x)),
  );
}

/** The letters were opened: the inbox stops counting what is on screen now. */
export async function markLettersSeen(): Promise<void> {
  const at = new Date().toISOString();
  store.set((s) => (s.inbox && (s.inbox.letters || s.inbox.corrections) ? { inbox: { ...s.inbox, letters: 0, corrections: 0 } } : {}));
  const d = db ?? (await getDB());
  await kvSet(d, 'lettersSeenAt', at);
}

// ---------- writes the server refused ----------

/**
 * A write the server refused for good (the sync engine parked it in kv deadWrites): kept in the
 * store for the screens that explain it (lib/rejected.ts, Asetukset → Hylätyt muutokset), and its
 * optimistic change is taken back where the device can: a new flag's unsent copy is forgotten
 * (FlagButton would show it as flagged for ever), a gloss approval or edit is undone in the overlay.
 */
export async function onWriteRejected(w: OutboxWrite): Promise<void> {
  store.set((s) => ({ rejected: [...s.rejected, w].slice(-50) }));
  for (const key of refusedFlagKeys([w], [...store.get().localFlags.values()])) await forgetLocalFlag(key);
  if (w.kind === 'gloss') await revertGloss(w.undo);
}

/** The device's unsent flags whose POST /api/flags was refused (made no later than the refused write). */
export function refusedFlagKeys(dead: readonly OutboxWrite[], flags: readonly LocalFlag[]): string[] {
  const byKey = new Map(flags.map((f) => [f.key, f]));
  const out = new Set<string>();
  for (const w of dead) {
    if (w.method !== 'POST' || w.path !== '/api/flags') continue;
    const target = (w.body as Partial<NewFlag> | undefined)?.target;
    if (!target?.table || !target.id) continue;
    const key = flagKey(target);
    const local = byKey.get(key);
    if (local && !local.serverId && local.createdAt <= w.createdAt) out.add(key);
  }
  return [...out];
}

/** What a gloss write takes back if refused: the overlay's row before it (null: there was none). */
export interface GlossUndo {
  id: string;
  prev: Gloss | null;
  /** updatedAt of the optimistic row: a row changed since (a later edit, a pull) is left alone. */
  at: string;
}

async function revertGloss(u: unknown): Promise<void> {
  const undo = u as GlossUndo | undefined;
  const s = store.get();
  const overlay = s.dict?.overlay;
  if (!undo?.id || !s.dict || !overlay) return;
  if (overlay.glosses[undo.id]?.updatedAt !== undo.at) return;
  const glosses = { ...overlay.glosses };
  if (undo.prev) glosses[undo.id] = undo.prev;
  else delete glosses[undo.id];
  const next: DictOverlay = { ...overlay, glosses };
  setDictionary(s.dict.bundle, next, s.bundleSource ?? 'bundle');
  if (db) await kvSet(db, 'dictOverlay', next);
}

/** Drop a device-local flag once the server's copy is known (FlagButton then shows the server's). */
export async function forgetLocalFlag(key: string): Promise<void> {
  if (!store.get().localFlags.has(key)) return;
  const flags = new Map(store.get().localFlags);
  flags.delete(key);
  store.set({ localFlags: flags });
  if (db) await db.delete('localFlags', key);
}

// ---------- dictionary edits (Finnish meanings) — offline via the outbox ----------

/**
 * Apply a change to the local dictionary overlay at once (the study dictionary and the lexicon
 * rebuild from it), so an approval or edit shows before the server has it. The next dictionary
 * pull replaces these rows with the server's.
 */
export async function patchOverlay(patch: { glosses?: Gloss[]; lemmas?: Lemma[] }): Promise<void> {
  const s = store.get();
  if (!s.dict) return;
  const base = s.dict.overlay ?? emptyOverlay(s.dict.generatedAt);
  const next: DictOverlay = {
    ...base,
    glosses: { ...base.glosses, ...Object.fromEntries((patch.glosses ?? []).map((g) => [g.id, g])) },
    lemmas: { ...base.lemmas, ...Object.fromEntries((patch.lemmas ?? []).map((l) => [l.id, l])) },
  };
  setDictionary(s.dict.bundle, next, s.bundleSource ?? 'bundle');
  if (db) await kvSet(db, 'dictOverlay', next);
}

/** "Hyväksy": a Finnish draft or AI-checked meaning becomes reviewed by me. */
export async function approveGloss(g: Gloss): Promise<void> {
  const me = store.get().me.id;
  const at = new Date().toISOString();
  const undo: GlossUndo = { id: g.id, prev: store.get().dict?.overlay?.glosses[g.id] ?? null, at };
  await patchOverlay({ glosses: [{ ...g, status: 'reviewed', reviewedBy: me, updatedAt: at }] });
  if (store.get().identity !== 'demo') await queueWrite('POST', `/api/glosses/${encodeURIComponent(g.id)}/approve`, undefined, 'gloss', { undo });
}

/**
 * Edit (or add) a Finnish meaning. `approve` = I vouch for it (status reviewed); otherwise it is
 * a draft again (also an AI-checked one: the check covered the old text) and waits for an approval.
 */
export async function saveFinnishGloss(g: Pick<Gloss, 'id' | 'lemmaId' | 'sense'>, text: string, approve: boolean): Promise<void> {
  const me = store.get().me.id;
  const at = new Date().toISOString();
  const row: Gloss = {
    id: g.id,
    lemmaId: g.lemmaId,
    lang: 'fi',
    sense: g.sense,
    text,
    status: approve ? 'reviewed' : 'draft',
    ...(approve ? { reviewedBy: me } : {}),
    updatedAt: at,
  };
  const undo: GlossUndo = { id: g.id, prev: store.get().dict?.overlay?.glosses[g.id] ?? null, at };
  await patchOverlay({ glosses: [row] });
  if (store.get().identity !== 'demo') {
    await queueWrite('PUT', `/api/glosses/${encodeURIComponent(g.id)}`, approve ? { text, status: 'reviewed' } : { text }, 'gloss', { undo });
  }
}

// ---------- notebook tasks ----------

/** Notebook rows kept on the device: this many days back (the server keeps them all). */
export const NOTEBOOK_KEEP_DAYS = 21;

/**
 * "Valmis" on a notebook task: kept on the device at once (kv 'notebook'), counted as studying
 * today (todayLocal.notebook, so the day shows as studied before any sync), and queued for
 * PUT /api/notebook (works offline; the server makes the day a study day). Once per task and day:
 * a second call changes nothing.
 */
export async function recordNotebookDone(row: NotebookDone): Promise<boolean> {
  const s = store.get();
  if (s.notebookDone.some((r) => r.day === row.day && r.taskKey === row.taskKey)) return false;
  const now = new Date();
  const date = todayLocal(now, s.pace.timezone);
  const oldest = addDaysLocal(date, -NOTEBOOK_KEEP_DAYS);
  const notebookDone = [...s.notebookDone.filter((r) => r.day >= oldest), row];
  const patch: Partial<AppState> = { notebookDone };
  if (row.day === date) {
    const t = s.todayLocal.date === date ? s.todayLocal : { date, reviews: 0, newWords: 0 };
    patch.todayLocal = { ...t, date, notebook: (t.notebook ?? 0) + 1 };
  }
  store.set(patch);
  const d = await liveDb();
  if (!d) return true;
  try {
    await kvSet(d, 'notebook', notebookDone);
    if (patch.todayLocal) await kvSet(d, 'todayLocal', store.get().todayLocal);
  } catch (err) {
    console.warn('[notebook] not written to the device', err);
  }
  if (s.identity !== 'demo') {
    const path = `/api/notebook/${encodeURIComponent(row.day)}/${encodeURIComponent(row.taskKey)}`;
    await queueWrite('PUT', path, { doneAt: row.doneAt, marks: row.marks }, 'notebook');
  }
  return true;
}

// ---------- lessons ----------

/**
 * Record a lesson check result (local first, then POST /api/sync via the lesson outbox).
 * Passing releases that lesson's words for this user. A pass is never taken back: attempts and
 * the best score accumulate, the earliest passedAt is kept (the server merges the same way).
 */
export async function recordLessonResult(lessonId: string, score: number, passed: boolean): Promise<LessonProgressRow> {
  const s = store.get();
  const prev = s.lessonProgress.find((l) => l.lessonId === lessonId);
  const row: LessonProgressRow = {
    lessonId,
    passedAt: prev?.passedAt ?? (passed ? new Date().toISOString() : null),
    bestScore: Math.max(prev?.bestScore ?? 0, score),
    attempts: (prev?.attempts ?? 0) + 1,
  };
  const lessonProgress = [...s.lessonProgress.filter((l) => l.lessonId !== lessonId), row];
  store.set({ lessonProgress });
  if (db) {
    await kvSet(db, 'lessonProgress', lessonProgress);
    if (s.identity !== 'demo') {
      await recordLessonProgress(db, row);
      await refreshPending();
      sync?.request(0);
    }
  }
  return row;
}

// ---------- sign out ----------

/** Service-worker runtime caches holding private data (vite.config.ts → runtimeCaching). */
export const PRIVATE_CACHES = ['recordings', 'recording-renders'] as const; // + trims waiting to upload (recordings/edits.ts)

export interface SignOutCheck {
  /** Changes still not on the server after a last sync attempt (they would be lost). */
  pending: number;
  /** No connection: Cloudflare Access cannot end the session (and signing in needs a connection). */
  offline: boolean;
}

/**
 * Before signing out: send what can still be sent, then say what would be lost. The caller
 * confirms with the person when `pending > 0` and refuses while `offline`.
 */
export async function signOutCheck(): Promise<SignOutCheck> {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (!offline && sync && store.get().identity !== 'demo') {
    try {
      await sync.syncNow();
    } catch {
      /* counted below */
    }
  }
  const d = db ?? (await getDB());
  const [r, w, l] = await Promise.all([d.count('reviewOutbox'), d.count('writeOutbox'), d.count('lessonOutbox')]);
  return { pending: r + w + l, offline: offline || store.get().sync.state === 'offline' };
}

/**
 * Sign in (shell/Login.tsx). On success the page is reloaded at the same address: the browser
 * sees the form submitted and the page navigate, which is when password managers offer to save
 * the login, and the app starts again as this person (refreshMe drops another person's data).
 * Throws the ApiError (bad_credentials, login_locked, offline) for the form to explain.
 */
export async function logIn(username: string, password: string): Promise<void> {
  await api.login(username.trim(), password);
  store.set({ needsLogin: false });
  if (!import.meta.env.VITEST) location.replace(location.pathname + location.search);
}

/** Vaihda salasana (Asetukset). Online only; throws the ApiError. */
export async function changePassword(current: string, next: string): Promise<void> {
  await api.changePassword(current, next);
}

/**
 * Sign out on this device: drop private caches (recordings), this person's cards, outboxes and
 * cached server lists (the dictionary stays), end the session on the server and start again at
 * the login screen. Call signOutCheck() first (components/SignOut.tsx): unsent changes are lost here.
 */
export async function signOut(): Promise<void> {
  // The next person on this device must not get this person's reminders.
  try {
    await disablePush();
  } catch {
    /* offline: the server drops the subscription when the push service says it is gone */
  }
  try {
    if (typeof caches !== 'undefined') await Promise.all(PRIVATE_CACHES.map((n) => caches.delete(n)));
  } catch {
    /* no Cache API */
  }
  try {
    const d = db ?? (await getDB());
    await clearUserData(d);
    await d.delete('kv', 'me');
  } catch {
    /* IndexedDB unavailable: the server logout still ends the session */
  }
  // an unfinished show-audio recording (Sarjan äänet) is this person's too
  try {
    await (await import('../showaudio/store')).dropCaptureData();
  } catch {
    /* nothing stored */
  }
  try {
    await api.logout();
  } catch {
    /* offline (SignOut refuses then) or already ended: the cookie expires on its own */
  }
  store.set({ needsLogin: true });
  if (!import.meta.env.VITEST) location.assign('/');
}

/** Forget a deleted recording's cached audio (the SW caches /api/recordings/:id/audio). */
export async function forgetRecordingAudio(id: string): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const c = await caches.open('recordings');
    await c.delete(`/api/recordings/${encodeURIComponent(id)}/audio`, { ignoreSearch: true });
  } catch {
    /* ignore */
  }
}
